import React from "react";
import { PulseTable, PulseTextField } from "@pulselabs-ai/shared-fe-ui";
import "./ProjectTable.scss";
import { useState } from "react";
import { findQuestionDetails, getAnswerText } from "../../utils";

export interface TableRow {
  surveyResponseId: string;
  createdAt: string;
  thumbnailUrl: string;
  videoUrl: string;
  userId: string;
  userMetadata: {
    deviceType: string;
    osType: string;
    version: string;
  };
  surveyResponse: ParsedSurveyResponse;
}

export interface SurveyQuestion {
  question: string;
  response: string;
  type: string;
  id: string;
  displayName: string;
}

// Add interface for survey element
interface SurveyElement {
  name: string;
  title: string;
  choices?: Array<{ value: string; text: string }>;
}

// Update the existing interfaces
export interface ParsedSurveyResponse {
  title: string;
  description: string;
  questions: SurveyQuestion[];
  [key: string]: string | SurveyQuestion[] | unknown; // More specific dynamic keys
}

type ProjectTableProps = {
  onRowClick?: (row: TableRow) => void;
  data?: TableRow[];
  loading?: boolean;
  surveySchema?: string | object;
};

const ProjectTable: React.FC<ProjectTableProps> = ({
  onRowClick,
  data = [],
  surveySchema,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  console.log("!data", data);
  // Filter data based on search query
  const filteredData = data.filter((item) => {
    console.log("item", item);
    // Search in main fields
    const mainFieldsMatch = Object.entries(item).some(([key, value]) => {
      // Skip surveyResponse field as we'll handle it separately
      if (key === "surveyResponse") return false;
      return value
        ?.toString()
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    });

    // Search in survey responses with mapped question titles
    const surveyResponsesMatch = Object.entries(item.surveyResponse).some(
      ([questionName, answer]) => {
        const questionDetails =
          surveySchema && findQuestionDetails(surveySchema, questionName);
        const questionTitle =
          (questionDetails && questionDetails.title) || questionName;
        const answerText = questionDetails
          ? getAnswerText(questionDetails, answer as string)
          : (answer as string);

        return (
          questionTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
          answerText.toLowerCase().includes(searchQuery.toLowerCase())
        );
      },
    );

    console.log("mainFieldsMatch", mainFieldsMatch);
    console.log("surveyResponsesMatch", surveyResponsesMatch);

    return mainFieldsMatch || surveyResponsesMatch;
  });

  // Generate dynamic columns from the survey schema
  const getDynamicColumns = () => {
    if (!surveySchema) return [];

    try {
      const schema =
        typeof surveySchema === "string"
          ? JSON.parse(surveySchema)
          : surveySchema;

      return schema.pages[0].elements.map((element: SurveyElement) => ({
        field: element.name,
        header: element.title,
        body: (item: TableRow) => {
          // Find the response for this question
          const response = item.surveyResponse[element.name];
          if (!response) return <div>-</div>;

          // Get the display text for the answer
          const answerText = element.choices
            ? getAnswerText({ choices: element.choices }, String(response))
            : String(response);

          return <div>{answerText}</div>;
        },
      }));
    } catch (error) {
      console.error("Error parsing survey schema:", error);
      return [];
    }
  };

  const videoColumn = {
    field: "thumbnailUrl",
    header: "Video",
    body: (item: TableRow) => (
      <div className="thumbnail-cell">
        {item.videoUrl.includes(".mp4") ? (
          // <img
          //   src={item.thumbnailUrl}
          //   alt="Video thumbnail"
          //   className="video-thumbnail"
          // />
          <video
            src={item.videoUrl}
            controls={false}
            controlsList="nodownload"
            className="video-preview"
            onError={(e) => console.error("Video error:", e)}
            playsInline
            muted
          ></video>
        ) : item.thumbnailUrl.includes(".png") ? (
          <img
            src={item.thumbnailUrl}
            alt="Video thumbnail"
            className="video-thumbnail"
          />
        ) : (
          <span>No image</span>
        )}
      </div>
    ),
  };

  const columns = [
    videoColumn,
    ...getDynamicColumns(),
    { field: "userId", header: "User ID", hidden: true },
    { field: "surveyResponseId", header: "ID", hidden: true },
    {
      field: "userMetadata.deviceType",
      header: "Device Type",
      body: (item: TableRow) => <div>{item.userMetadata.deviceType}</div>,
    },
    {
      field: "userMetadata.osType",
      header: "Device OS",
      body: (item: TableRow) => <div>{item.userMetadata.osType}</div>,
    },
    {
      field: "userMetadata.version",
      header: "OS Version",
      body: (item: TableRow) => <div>{item.userMetadata.version}</div>,
    },
    { field: "createdAt", header: "Created At", dateFormat: "MM/DD/YYYY" },
  ];

  return (
    <div className="project-table-container">
      <div className="table-header">
        <div className="results-count">
          Displaying {filteredData.length} out of {data.length} Results
        </div>
        <div className="search-field">
          <PulseTextField
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <PulseTable
        className="project-table"
        value={filteredData}
        columnsDef={columns}
        onRowClick={(event) => onRowClick?.(event.data as TableRow)}
        rowHover={true}
        style={{ cursor: "pointer" }}
        paginator={false}
        rows={10000}
        scrollHeight="calc(100vh - 445px)"
      />
    </div>
  );
};

export default ProjectTable;
